// src/api/levelApi.js
// Cliente para api_level.py (FastAPI)
//
// Endpoints:
// - POST /api/analyze (FormData: video, player_name, multi)
// - GET  /api/result/{job_id} (status.json)
// - GET  /api/download/{job_id}/resultado.json (resultado final)
// - GET  /api/download/{job_id}/thumb_###.jpg (thumbnails multi)
// - GET  /api/analyses?limit=50  (filtrado por JWT en backend)
// - GET  /api/series?metric=posture&limit=50  (filtrado por JWT en backend)
//
// Config:
// - VITE_LEVEL_API_BASE=http://localhost:8000  (sin slash final)

import { supabase } from '../lib/supabase.js'
import { captureMessage } from '@sentry/react'

const BASE = '' // SIEMPRE same-origin

// Set by PlayerProvider on session changes so we don't call getSession() on every request
let _cachedToken = null
export function _setApiToken(token) { _cachedToken = token }

// Caducidad (exp, en segundos) del JWT de Supabase; 0 si no se puede leer
function _tokenExp(tok) {
  try { return JSON.parse(atob(tok.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).exp || 0 } catch { return 0 }
}

// Token al día desde Supabase (getSession lo renueva si ha caducado; force:
// refreshSession). Con tiempo límite para no colgarse en Safari.
async function _freshToken(force = false) {
  try {
    const timeout = new Promise(resolve => setTimeout(() => resolve({ data: { session: null } }), 4000))
    const { data: { session } } = await Promise.race([force ? supabase.auth.refreshSession() : supabase.auth.getSession(), timeout])
    if (session?.access_token) _cachedToken = session.access_token
    return session?.access_token || null
  } catch {
    return null
  }
}

async function getAuthHeaders() {
  let tok = _cachedToken
  // Con el móvil en reposo se paran los temporizadores que renuevan la sesión
  // y el token guardado se queda caducado: al volver, la primera petición daba
  // 401 ("Couldn't complete that", torneo de Oxygen 24/10). Si caduca en menos
  // de un minuto, se pide uno al día antes de llamar.
  if (!tok || _tokenExp(tok) * 1000 < Date.now() + 60000) tok = (await _freshToken()) || tok
  return tok ? { Authorization: `Bearer ${tok}` } : {}
}

async function parseBody(res) {
  const t = await res.text()
  if (!t) return null
  try {
    return JSON.parse(t)
  } catch {
    return t
  }
}

async function http(path, opts = {}) {
  const authHeaders = await getAuthHeaders()
  const send = (h) => fetch(`${BASE}${path}`, { ...opts, headers: { ...(opts.headers || {}), ...h } })
  let res = await send(authHeaders)
  // 401 con sesión: se renueva y se repite una vez (sin que el usuario lo note)
  if (res.status === 401 && authHeaders.Authorization) {
    const tok = await _freshToken(true)
    if (tok && `Bearer ${tok}` !== authHeaders.Authorization) res = await send({ Authorization: `Bearer ${tok}` })
  }

  const data = await parseBody(res)
  if (!res.ok) {
    const msg = (data && (data.detail || data.error || data.message)) || `HTTP ${res.status}`
    // A Sentry solo lo que es un fallo de verdad: el servidor (5xx) o la sesión
    // rechazada aun después de renovarla. Los errores con nombre ("full",
    // "not_in"…) son respuestas normales y no se mandan.
    if (res.status >= 500 || (res.status === 401 && authHeaders.Authorization)) {
      try {
        captureMessage(`API ${res.status} ${(opts.method || 'GET').toUpperCase()} ${path.split('?')[0].replace(/\/\d+/g, '/:id')}`, {
          level: res.status >= 500 ? 'error' : 'warning',
          extra: { path, status: res.status, detail: typeof msg === 'string' ? msg.slice(0, 200) : String(res.status) },
        })
      } catch { /* sin Sentry */ }
    }
    // Errores de partidos/torneos con nombre: "código|nombre"
    const [code, who] = typeof msg === 'string' && msg.includes('|') ? msg.split('|') : [msg, undefined]
    const err = new Error(code)
    if (who) err.who = who
    throw err
  }
  return data
}

export async function analyzeVideo({ videoFile, playerName = "", multi = false } = {}) {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token

  const fd = new FormData()
  fd.append("video", videoFile)
  fd.append("player_name", playerName)
  fd.append("multi", multi ? "true" : "false")

  const headers = {}
  if (token) headers["Authorization"] = `Bearer ${token}`

  return fetch("/api/analyze", {
    method: "POST",
    body: fd,
    headers,
  }).then(r => r.json())
}

// forPlayerId: profesor que analiza para un jugador de su club (el análisis
// va a la cuenta del jugador; ver for_player_id en /api/analyze)
export function analyzeVideoWithProgress({ videoFile, playerName = '', studentName = '', forPlayerId = null, multi = false, force = false, handedness = 'right', onProgress } = {}) {
  if (!videoFile) return Promise.reject(new Error('Falta videoFile'))

  const fd = new FormData()
  fd.append('video', videoFile)
  fd.append('player_name', playerName)
  if (studentName) fd.append('student_name', studentName)
  if (forPlayerId) fd.append('for_player_id', String(forPlayerId))
  fd.append('multi', multi ? 'true' : 'false')
  if (force) fd.append('force', 'true')
  fd.append('handedness', handedness)

  return new Promise((resolve, reject) => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const token = session?.access_token

      const xhr = new XMLHttpRequest()
      xhr.open('POST', `${BASE}/api/analyze`, true)

      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`)
      }

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && typeof onProgress === 'function') {
          const pct = Math.round((event.loaded / event.total) * 100)
          onProgress(pct)
        }
      }

      xhr.onload = () => {
        try {
          const data = xhr.responseText ? JSON.parse(xhr.responseText) : null
          if (xhr.status >= 200 && xhr.status < 300) resolve(data)
          else reject(new Error(data?.detail || `HTTP ${xhr.status}`))
        } catch (e) {
          reject(e)
        }
      }

      xhr.onerror = () => reject(new Error('Error de red'))

      xhr.send(fd)
    }).catch(reject)
  })
}

export async function getStatus(jobId) {
  if (!jobId) throw new Error('Falta jobId')
  return http(`/api/result/${encodeURIComponent(jobId)}`)
}

// ✅ alias útil para el Dashboard (yo lo uso como downloadResultJson)
export async function downloadResultJson(jobId) {
  return getResultado(jobId)
}

export async function getResultado(jobId) {
  if (!jobId) throw new Error('Falta jobId')
  return http(`/api/download/${encodeURIComponent(jobId)}/resultado.json`)
}

export async function deleteJob(jobId) {
  if (!jobId) throw new Error('Falta jobId')
  return http(`/api/job/${encodeURIComponent(jobId)}`, { method: 'DELETE' })
}

// El usuario elimina su propia cuenta desde Perfil (api/routes_profile.py)
export async function deleteMyAccount() {
  return http('/api/me/delete-account', { method: 'POST' })
}

export async function registerDeviceToken(token, platform = 'ios') {
  if (!token) return
  return http('/api/device-token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, platform }),
  })
}

// ── Web Push: navegador/PWA y Android (TWA sobre Chrome, mismo mecanismo,
// no hace falta Firebase) ────────────────────────────────────────────────
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || ''

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)))
}

// Pide permiso y suscribe al navegador a Web Push, registrando la suscripción
// en el backend. No hace nada dentro de la app nativa de iOS (esa usa APNs
// vía el puente nativo, ver window.__nivelaSetDeviceToken más abajo).
// Devuelve true si quedó suscrito, false si no fue posible (sin soporte,
// permiso denegado, o dentro de la app nativa).
export async function subscribeWebPush() {
  if (window.webkit?.messageHandlers?.nativeCamera) return false
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window) || !VAPID_PUBLIC_KEY) {
    return false
  }
  try {
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') return false
    const registration = await navigator.serviceWorker.ready
    let subscription = await registration.pushManager.getSubscription()
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      })
    }
    await registerDeviceToken(JSON.stringify(subscription), 'web')
    return true
  } catch {
    return false
  }
}

// ── Estado del permiso de notificaciones en este dispositivo ─────────────
// Web: granted / denied / default (aún no ha contestado) / unsupported.
// App nativa de iOS: native_* si la build trae el puente "pushPermission"
// (ver WebView.swift); en builds anteriores no se puede saber → native_unknown.
let _nativePushState = null
const _nativePushListeners = new Set()

export function isNativeIosApp() {
  return !!window.webkit?.messageHandlers?.nativeCamera
}

// Versión de la app nativa (iOS y Android): ver lib/nativeApp.js
export { nativeAppInfo, nativeBuildAtLeast } from '../lib/nativeApp.js'

export function pushPermissionState() {
  if (isNativeIosApp()) return _nativePushState || 'native_unknown'
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window) || !VAPID_PUBLIC_KEY) {
    return 'unsupported'
  }
  return Notification.permission
}

export function onNativePushPermission(fn) {
  _nativePushListeners.add(fn)
  return () => _nativePushListeners.delete(fn)
}

// iOS: pedir el permiso (si aún no se ha contestado) o abrir Ajustes (si se denegó).
export function nativePushRequest() { window.webkit?.messageHandlers?.pushPermission?.postMessage({ action: 'request' }) }
export function nativePushOpenSettings() { window.webkit?.messageHandlers?.pushPermission?.postMessage({ action: 'openSettings' }) }

// iPhone/iPad en el navegador (no instalada): las notificaciones web solo
// funcionan con la app añadida a la pantalla de inicio (iOS 16.4 o posterior).
// 'needs_install' | 'too_old' | null (no es el caso)
export function iosInstallState() {
  if (isNativeIosApp()) return null
  const ua = navigator.userAgent || ''
  const ios = /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1)
  if (!ios) return null
  const standalone = window.navigator.standalone === true || window.matchMedia?.('(display-mode: standalone)')?.matches
  if (standalone) return null
  const m = ua.match(/OS (\d+)[_.](\d+)/)
  if (m && (Number(m[1]) < 16 || (Number(m[1]) === 16 && Number(m[2]) < 4))) return 'too_old'
  return 'needs_install'
}

export function pushPlatform() {
  if (isNativeIosApp()) return 'ios_app'
  const ua = navigator.userAgent || ''
  if (/iPhone|iPad|iPod/i.test(ua)) return (window.navigator.standalone ? 'ios_pwa' : 'ios_safari')
  if (/Android/i.test(ua)) return 'android'
  return 'desktop'
}

// Qué pasa con los avisos de "activa las notificaciones" (Admin → push), sin esperar
export function logPushPrompt(context, event) {
  http('/api/me/push-prompt', _json({ context, event, platform: pushPlatform() })).catch(() => {})
}

export async function reportPushPermission() {
  try {
    await http('/api/player/push-permission', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ state: pushPermissionState() }),
    })
  } catch { /* solo informativo */ }
}

// Por defecto solo los análisis en que juega el dueño de la cuenta (los de su
// nivel); includeOthers añade los de alumnos/otros subidos desde la cuenta.
export async function getAnalyses({ limit = 10, studentName = null, linkedPlayerId = null, includeOthers = false } = {}) {
  const qs = new URLSearchParams()
  qs.set('limit', String(limit))
  if (includeOthers) qs.set('include_others', '1')
  // Alumno vinculado a una cuenta real: prioridad sobre el nombre de texto
  // (mismo criterio que la lista de Alumnos, ver coach_list_students).
  if (linkedPlayerId) qs.set('linked_player_id', String(linkedPlayerId))
  else if (studentName) qs.set('student_name', studentName)
  return http(`/api/analyses?${qs.toString()}`)
}

// Estadísticas de "Tu actividad" sobre TODOS los análisis propios (no una
// página limitada) — ver get_own_activity_stats en engine/db.py.
export async function getAnalysesStats() {
  return http('/api/analyses/stats')
}

// Foto de perfil: URL determinista por uid, sin necesidad de columna en DB
// (ver /avatar/{uid}.jpg en api/routes_api.py). El componente que la usa cae
// a las iniciales con onError si el jugador no tiene foto (404).
export function avatarUrl(uid, version) {
  return `/api/avatar/${uid}.jpg${version ? `?v=${version}` : ''}`
}

export async function uploadAvatar(file) {
  const fd = new FormData()
  fd.append('file', file)
  return http('/api/player/avatar', { method: 'PUT', body: fd })
}

export async function deleteAvatar() {
  return http('/api/player/avatar', { method: 'DELETE' })
}

// ── Media token cache (reemplaza JWT en URLs de descarga) ───────────────────
let _mtoken = null
let _mtokenExp = 0

async function _refreshMediaToken() {
  try {
    const result = await http('/api/media-token')
    _mtoken = result.token
    _mtokenExp = Date.now() + result.ttl_ms - 30000  // renovar 30s antes de expirar
  } catch { /* ignorar — downloadUrl usará la URL sin token */ }
}

// Obtener o renovar el media token de forma transparente
export async function ensureMediaToken() {
  if (_mtoken && Date.now() < _mtokenExp) return _mtoken
  await _refreshMediaToken()
  return _mtoken
}

// Limpiar al cerrar sesión, renovar al iniciarla o al refrescar JWT
supabase.auth.onAuthStateChange((event) => {
  if (event === 'SIGNED_OUT') { _mtoken = null; _mtokenExp = 0 }
  if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED') _refreshMediaToken()
})

// ── Puente con la app nativa de iOS: recibe el device token de push (APNs) ──
let _nativeDeviceToken = null
let _nativeDeviceTokenSent = false

async function _sendNativeDeviceTokenIfReady() {
  if (!_nativeDeviceToken || _nativeDeviceTokenSent) return
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) return
  try {
    await registerDeviceToken(_nativeDeviceToken, 'ios')
    _nativeDeviceTokenSent = true
  } catch { /* se reintentará en el próximo cambio de sesión o recarga de página */ }
}

// ── Web Push al iniciar sesión ───────────────────────────────────────────
// Ya NO se pide el permiso aquí: preguntarlo nada más entrar, sin contexto,
// es cuando más se rechaza, y una vez bloqueado el navegador no deja volver
// a preguntar. Ahora lo pide PushPromptCard, explicando para qué sirve, en
// el momento en que tiene sentido (2026-10-08). Aquí solo se renueva la
// suscripción de quien ya lo concedió y se informa al servidor del estado.
let _webPushAutoTried = false

async function _autoSubscribeWebPushIfEnabled() {
  if (_webPushAutoTried) return
  _webPushAutoTried = true
  try {
    // Sin sesión no hay a quién apuntarle el permiso (y el servidor responde 401).
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) { _webPushAutoTried = false; return }
    if (pushPermissionState() === 'granted') {
      const profile = await getPlayerProfile()
      if (profile?.notify_push !== false) await subscribeWebPush()   // no pregunta: ya está concedido
    }
    await reportPushPermission()
  } catch { /* silencioso: no bloquear el arranque de la app por esto */ }
}

if (typeof window !== 'undefined') {
  window.__nivelaSetDeviceToken = (token) => {
    _nativeDeviceToken = token
    _sendNativeDeviceTokenIfReady()
  }
  // Pide activamente el token nativo por si ya llegó antes de que este módulo se cargara
  // (la app nativa lo reenvía al recibir este mensaje).
  window.webkit?.messageHandlers?.requestDeviceToken?.postMessage({})
  // Estado del permiso de notificaciones en iOS (solo builds con el puente).
  window.__nivelaSetPushPermission = (state) => {
    _nativePushState = `native_${state}`
    _nativePushListeners.forEach(fn => { try { fn(_nativePushState) } catch { /* ignore */ } })
    reportPushPermission()
  }
  window.webkit?.messageHandlers?.pushPermission?.postMessage({ action: 'status' })
  // Al volver a la app (p. ej. desde Ajustes) se vuelve a consultar.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      window.webkit?.messageHandlers?.pushPermission?.postMessage({ action: 'status' })
    }
  })
}

supabase.auth.onAuthStateChange((event) => {
  if (event === 'SIGNED_OUT') { _nativeDeviceTokenSent = false; _webPushAutoTried = false }
  if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED') {
    _sendNativeDeviceTokenIfReady()
    _autoSubscribeWebPushIfEnabled()
  }
})

export function downloadUrl(jobId, filename, { dl = false } = {}) {
  if (!jobId) throw new Error("Falta jobId");
  const safe = String(filename)
    .split("/")
    .map((p) => encodeURIComponent(p))
    .join("/");
  const base = `${BASE}/api/download/${encodeURIComponent(jobId)}/${safe}`;
  const qs = new URLSearchParams();
  if (_mtoken) qs.set('mtoken', _mtoken);
  if (dl) qs.set('dl', '1');
  const query = qs.toString();
  return query ? `${base}?${query}` : base;
}

export async function downloadVideoUrl(jobId, filename) {
  await ensureMediaToken();
  return downloadUrl(jobId, filename, { dl: true });
}

export function shareClipUrl(jobId, filename) {
  if (!jobId) throw new Error("Falta jobId");
  const safe = String(filename)
    .split("/")
    .map((p) => encodeURIComponent(p))
    .join("/");
  const base = `${BASE}/api/share-clip/${encodeURIComponent(jobId)}/${safe}`;
  const qs = new URLSearchParams();
  if (_mtoken) qs.set('mtoken', _mtoken);
  const query = qs.toString();
  return query ? `${base}?${query}` : base;
}

export async function fetchShareClipBlob(jobId, filename) {
  await ensureMediaToken();
  const url = shareClipUrl(jobId, filename);
  const authHeaders = await getAuthHeaders();
  const r = await fetch(url, { headers: authHeaders });
  if (!r.ok) throw new Error(`share-clip ${r.status}`);
  return r.blob();
}

export async function getSeries({ metric, limit = 50 }) {
  const qs = new URLSearchParams()
  qs.set('metric', metric)
  qs.set('limit', String(limit))
  return http(`/api/series?${qs.toString()}`)
}

export async function fetchSeries({ metric, limit = 50 }) {
  const authHeaders = await getAuthHeaders()
  const qs = new URLSearchParams()
  qs.set('metric', metric)
  qs.set('limit', String(limit))

  const r = await fetch(`/api/series?${qs.toString()}`, { headers: authHeaders })
  if (!r.ok) throw new Error(`series ${r.status}`)
  return r.json()
}

// ─────────────────────────────────────────────────────────────────────────────
// GRUPOS
// ─────────────────────────────────────────────────────────────────────────────

export async function crearGrupo({ nombre }) {
  return http('/api/grupos', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ nombre }),
  })
}

export async function getMisGrupos() {
  return http('/api/grupos')
}

export async function getGrupoPublico(inviteCode) {
  // Sin auth (para el flujo de invitación)
  const res = await fetch(`/api/grupos/invitacion/${encodeURIComponent(inviteCode)}`)
  const data = await res.json().catch(() => null)
  if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`)
  return data
}

export async function getGrupoRanking(grupoId) {
  return http(`/api/grupos/${grupoId}/ranking`)
}

export async function getGrupoMiembros(grupoId) {
  return http(`/api/grupos/${grupoId}/miembros`)
}

export async function getGrupoMetricas(grupoId) {
  return http(`/api/grupos/${grupoId}/metricas`)
}

export async function getGrupoEvolucion(grupoId) {
  return http(`/api/grupos/${grupoId}/evolucion`)
}

export async function unirseGrupo(grupoId) {
  return http(`/api/grupos/${grupoId}/unirse`, { method: 'POST' })
}

export async function salirGrupo(grupoId) {
  return http(`/api/grupos/${grupoId}/salir`, { method: 'DELETE' })
}

export async function buscarJugadores(q) {
  return http(`/api/jugadores/buscar?q=${encodeURIComponent(q)}`)
}

export async function invitarJugadorAGrupo(grupoId, targetSupabaseUserId) {
  return http(`/api/grupos/${grupoId}/invitar`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ target_supabase_user_id: targetSupabaseUserId }),
  })
}

export async function getGrupoFeed(grupoId) {
  return http(`/api/grupos/${grupoId}/feed`)
}

export async function reactFeedItem(grupoId, feedId, emoji) {
  return http(`/api/grupos/${grupoId}/feed/${feedId}/react`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ emoji }),
  })
}

export async function getGrupoRetos(grupoId) {
  return http(`/api/grupos/${grupoId}/retos`)
}

export async function crearReto(grupoId, { metric, challenged_slug }) {
  return http(`/api/grupos/${grupoId}/retos`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ metric, challenged_slug }),
  })
}

export async function getGrupoProgreso(grupoId) {
  return http(`/api/grupos/${grupoId}/progreso`)
}

export async function crearRetoPublico({ score, label, jobId }) {
  return http('/api/retos/publico', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ score, label, job_id: jobId || '' }),
  })
}

export async function getRetoPublico(shareCode) {
  const res = await fetch(`/api/retos/publico/${encodeURIComponent(shareCode)}`)
  const data = await res.json().catch(() => null)
  if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`)
  return data
}

/**
 * ❌ Eliminado (legacy) — ya no lo necesitas si usas multi=true en /api/analyze
 * export async function analyzeLongVideo ...
 * export async function fetchLongResult ...
 */

// ─────────────────────────────────────────────────────────────────────────────
// COACH — ALUMNOS Y GRUPOS
// ─────────────────────────────────────────────────────────────────────────────

export async function coachGetStudents() {
  return http('/api/coach/students')
}

export async function coachGetClubStudents() {
  return http('/api/coach/club-students')
}

export async function coachCreateStudent({ name, notes = '', email = '', linkedPlayerId = null }) {
  return http('/api/coach/students', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, notes, email, linked_player_id: linkedPlayerId }),
  })
}

export async function coachUpdateStudent(id, { name, notes = '', email = '', linkedPlayerId = null }) {
  return http(`/api/coach/students/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, notes, email, linked_player_id: linkedPlayerId }),
  })
}

export async function coachSearchClubPlayers(query = '') {
  const qs = new URLSearchParams()
  if (query) qs.set('q', query)
  return http(`/api/coach/club-players?${qs.toString()}`)
}

export async function coachDeleteStudent(id) {
  return http(`/api/coach/students/${id}`, { method: 'DELETE' })
}

export async function coachGetGroups() {
  return http('/api/coach/groups')
}

export async function coachCreateGroup({ name }) {
  return http('/api/coach/groups', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  })
}

export async function coachUpdateGroup(id, { name }) {
  return http(`/api/coach/groups/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  })
}

export async function coachDeleteGroup(id) {
  return http(`/api/coach/groups/${id}`, { method: 'DELETE' })
}

export async function getExercises(metric, stroke = 'forehand', lang = 'es') {
  const qs = new URLSearchParams({ metric, stroke, lang })
  return http(`/api/exercises?${qs.toString()}`)
}

export async function coachAddMember(groupId, studentId) {
  return http(`/api/coach/groups/${groupId}/members/${studentId}`, { method: 'POST' })
}

export async function coachRemoveMember(groupId, studentId) {
  return http(`/api/coach/groups/${groupId}/members/${studentId}`, { method: 'DELETE' })
}

export async function getGlobalRanking(limit = 20) {
  return http(`/api/ranking/global?limit=${limit}`)
}

export async function getPlayerProfile() {
  return http('/api/player/profile')
}

// Comprobación pura (no crea nada) de si el usuario autenticado ya tiene
// cuenta en `players` — ver Login.jsx, uso tras signInWithOAuth para cerrar
// el hueco del botón "Iniciar sesión" con Google/Apple (que no pide código).
export async function playerExists() {
  const r = await http('/api/player/exists')
  return !!r?.exists
}

// Términos y privacidad: versión vigente + URL de cada documento (pública,
// la usa el registro antes de que haya sesión), qué le falta por aceptar al
// usuario, y registro de la aceptación. Ver components/LegalGate.jsx.
export async function getLegalCurrent(lang = '') {
  return http(`/api/legal/current?lang=${encodeURIComponent(lang)}`)
}

export async function getLegalStatus(lang = '') {
  return http(`/api/legal/status?lang=${encodeURIComponent(lang)}`)
}

export async function acceptLegal({ terms, privacy, source = 'reaccept', locale = '' }) {
  return http('/api/legal/accept', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ terms, privacy, source, locale }),
  })
}

export async function setPlayerCity({ city, country, province = '' }) {
  return http('/api/player/city', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ city, country, province }),
  })
}

export async function getClubs() {
  return http('/api/clubs')
}

export async function setPlayerClub(clubId) {
  return http('/api/player/club', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ club_id: clubId }),
  })
}

// ── Clubes: pertenencia, permiso para los profesores y panel del club
// (engine/club_members.py, api/routes_club_members.py)
const _cid = (id) => `/api/clubs/${encodeURIComponent(id)}`
export async function getMyClubs() { return http('/api/clubs/me') }
export async function joinClub(clubId) { return http(`${_cid(clubId)}/join`, _json({})) }
export async function setActiveClub(clubId) { return http(`${_cid(clubId)}/active`, _json({})) }
export async function leaveClub(clubId) { return http(`${_cid(clubId)}/leave`, _json({})) }
export async function setCoachAccess(clubId, granted) { return http(`${_cid(clubId)}/coach-access`, _json({ granted })) }
export async function answerClubInvite(inviteId, accept) {
  return http(`/api/clubs/invites/${encodeURIComponent(inviteId)}/answer`, _json({ accept }))
}
export async function markClubNoticeSeen(clubId) { return http(`${_cid(clubId)}/notice-seen`, _json({})) }
export async function setWantsLessons(clubId, on) { return http(`${_cid(clubId)}/wants-lessons`, _json({ on })) }
export async function getClubPanel(clubId) { return http(`${_cid(clubId)}/panel`) }
export async function decideClubRequest(clubId, playerId, accept) {
  return http(`${_cid(clubId)}/requests/${encodeURIComponent(playerId)}`, _json({ accept }))
}
export async function inviteToClub(clubId, body) { return http(`${_cid(clubId)}/invites`, _json(body)) }
export async function revokeClubInvite(clubId, inviteId) {
  return http(`${_cid(clubId)}/invites/${encodeURIComponent(inviteId)}`, { method: 'DELETE' })
}
export async function setClubMemberRoles(clubId, playerId, isTeacher, isAdmin) {
  return http(`${_cid(clubId)}/members/${encodeURIComponent(playerId)}/roles`, _json({ is_teacher: isTeacher, is_admin: isAdmin }))
}
export async function removeClubMember(clubId, playerId) {
  return http(`${_cid(clubId)}/members/${encodeURIComponent(playerId)}`, { method: 'DELETE' })
}

export async function setRankingVisibility(visible) {
  return http('/api/player/ranking-visibility', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ visible }),
  })
}

export async function setNotifyAnalysisReady(enabled) {
  return http('/api/player/notify-analysis-ready', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled }),
  })
}

export async function setNotifyPush(enabled) {
  return http('/api/player/notify-push', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled }),
  })
}

export async function setNotifyGroupFeed(enabled) {
  return http('/api/player/notify-group-feed', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled }),
  })
}

export async function getCityRanking(city, country, limit = 50) {
  const qs = new URLSearchParams({ city, country, limit })
  return http(`/api/ranking/ciudad?${qs.toString()}`)
}

export async function getProvinceRanking(province, country, limit = 50) {
  const qs = new URLSearchParams({ province, country, limit })
  return http(`/api/ranking/provincia?${qs.toString()}`)
}

export async function getCountryRanking(country, limit = 50) {
  const qs = new URLSearchParams({ country, limit })
  return http(`/api/ranking/pais?${qs.toString()}`)
}

export async function getClubRanking(limit = 50) {
  return http(`/api/ranking/club?limit=${limit}`)
}

export async function getMyMonthProgress() {
  return http('/api/me/progreso-mensual')
}

export async function getReliability() {
  return http('/api/reliability')
}

// "Mi plan": focos de mejora guardados que avanzan con cada vídeo (engine/plan.py)
// Referencia por golpe para la gravedad de las mejoras (cambia poco: caché en memoria)
const _planRefCache = new Map()
export function getPlanReference(stroke) {
  if (!_planRefCache.has(stroke)) {
    _planRefCache.set(stroke, http(`/api/plan/reference?stroke=${encodeURIComponent(stroke)}`)
      .catch(e => { _planRefCache.delete(stroke); throw e }))
  }
  return _planRefCache.get(stroke)
}

// Plan de mejora de un alumno con cuenta (solo su profesor)
export async function getStudentPlan(linkedPlayerId) {
  return http(`/api/coach/student-plan?linked_player_id=${encodeURIComponent(linkedPlayerId)}`)
}

// Idioma de la app, para recibir los avisos push en ese idioma
export async function setPlayerLang(lang) {
  return http('/api/player/lang', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ lang }),
  })
}

// El profesor en el plan del alumno: fijar/quitar focos y dejar/borrar notas
export async function coachPinFocus({ linkedPlayerId, stroke, metric }) {
  return http('/api/coach/student-plan/focus', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ linked_player_id: linkedPlayerId, stroke, metric }),
  })
}
export async function coachUnpinFocus(focusId, linkedPlayerId) {
  return http(`/api/coach/student-plan/focus/${focusId}?linked_player_id=${encodeURIComponent(linkedPlayerId)}`, { method: 'DELETE' })
}
export async function coachAddPlanNote({ linkedPlayerId, text, focusId = null }) {
  return http('/api/coach/student-plan/note', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ linked_player_id: linkedPlayerId, text, focus_id: focusId }),
  })
}
// "Este golpe no es un X" desde Alumnos, en el plan de un alumno
export async function coachReportPlanExample({ linkedPlayerId, runId, shotIndex, stroke }) {
  return http('/api/coach/student-plan/example-report', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ linked_player_id: linkedPlayerId, run_id: runId, shot_index: shotIndex, stroke }),
  })
}
export async function coachDeletePlanNote(noteId, linkedPlayerId) {
  return http(`/api/coach/student-plan/note/${noteId}?linked_player_id=${encodeURIComponent(linkedPlayerId)}`, { method: 'DELETE' })
}

// "Entrena ahora": apunta la sesión hecha de un foco
export async function logPlanTraining(focusId) {
  return http('/api/me/plan/training', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ focus_id: focusId }),
  })
}

// "Este golpe no es un X" bajo un clip de ejemplo de Mi plan
export async function reportPlanExample({ runId, shotIndex, stroke }) {
  return http('/api/me/plan/example-report', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ run_id: runId, shot_index: shotIndex, stroke }),
  })
}

// Resultado de los focos de Mi plan en los golpes de un análisis
export async function getPlanTest(jobId) {
  return http(`/api/me/plan/test?job_id=${encodeURIComponent(jobId)}`)
}

// Actividad para Admin (engine/activity.py): 'app' al abrir la app (como mucho
// una vez cada 30 min por dispositivo, al volver a primer plano también) y
// 'plan' cada vez que se entra en Mi plan. Nunca falla hacia el usuario.
export function pingActivity(kind = 'app') {
  if (kind === 'app') {
    const k = 'nivela_activity_app_at'
    try {
      const last = Number(localStorage.getItem(k) || 0)
      if (Date.now() - last < 30 * 60 * 1000) return
      localStorage.setItem(k, String(Date.now()))
    } catch { /* sin almacenamiento: se manda igual */ }
  }
  http('/api/me/activity', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ kind }) }).catch(() => {})
}

export async function getMyFullPlan() {
  return http('/api/me/plan/full')
}

export async function getPlayerLevel() {
  return http('/api/player/level')
}

export async function getStudentLevel({ linkedPlayerId, studentName } = {}) {
  const qs = linkedPlayerId
    ? `linked_player_id=${encodeURIComponent(linkedPlayerId)}`
    : `student_name=${encodeURIComponent(studentName)}`
  return http(`/api/coach/student-level?${qs}`)
}

// Golpes válidos de un tipo (forehand, backhand, volea, bandeja, smash, serve)
// de un alumno, con job_id e índice para el clip. Ver get_player_shots.
export async function getStudentShots({ linkedPlayerId, studentName, stroke } = {}) {
  const qs = new URLSearchParams({ stroke })
  if (linkedPlayerId) qs.set('linked_player_id', String(linkedPlayerId))
  else qs.set('student_name', studentName)
  return http(`/api/coach/student-shots?${qs.toString()}`)
}

export async function getStudentReliability({ linkedPlayerId, studentName } = {}) {
  const qs = linkedPlayerId
    ? `linked_player_id=${encodeURIComponent(linkedPlayerId)}`
    : `student_name=${encodeURIComponent(studentName)}`
  return http(`/api/coach/student-reliability?${qs}`)
}

export async function uploadShareCard(blob) {
  const authHeaders = await getAuthHeaders()
  const fd = new FormData()
  fd.append('file', new File([blob], 'share.png', { type: 'image/png' }))
  const res = await fetch(`${BASE}/api/share`, { method: 'POST', body: fd, headers: authHeaders })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json()
}

// ── Partidos de club ──
export async function getClubMatches() {
  return http('/api/club/matches')
}
// ── Torneos Americanos (engine/tournaments.py) ──
export async function getClubTournaments() { return http('/api/club/tournaments') }
export async function getMyTournaments() { return http('/api/club/tournaments/mine') }
// code: enlace o código compartido (/t/CODIGO), para verlo y apuntarse desde fuera del club
export async function getClubTournament(id, code = null) { return http(`/api/club/tournaments/${encodeURIComponent(id)}${code ? `?code=${encodeURIComponent(code)}` : ''}`) }
export async function getTournamentByCode(code) { return http(`/api/club/tournaments/code/${encodeURIComponent(code)}`) }
export async function notifyTournament(id) { return http(`/api/club/tournaments/${encodeURIComponent(id)}/notify`, _json({})) }
// Ranking por fases de club (engine/ranking.py)
export async function getPhaseRankings() { return http('/api/club/rankings') }
export async function createPhaseRanking(body) { return http('/api/club/rankings', _json(body)) }
export async function getPhaseRanking(id) { return http(`/api/club/rankings/${encodeURIComponent(id)}`) }
export async function addRankingPair(id, body) { return http(`/api/club/rankings/${encodeURIComponent(id)}/pairs`, _json(body)) }
export async function addRankingPhase(id, body) { return http(`/api/club/rankings/${encodeURIComponent(id)}/phases`, _json(body)) }
export async function setRankingGroups(phaseId, body) { return http(`/api/club/ranking-phases/${encodeURIComponent(phaseId)}/groups`, _json(body)) }
export async function openRankingPhase(phaseId) { return http(`/api/club/ranking-phases/${encodeURIComponent(phaseId)}/open`, _json({})) }
export async function closeRankingPhase(phaseId) { return http(`/api/club/ranking-phases/${encodeURIComponent(phaseId)}/close`, _json({})) }
export async function reportRankingResult(matchId, body) { return http(`/api/club/ranking-matches/${encodeURIComponent(matchId)}/result`, _json(body)) }
const _rkm = (id, action) => `/api/club/ranking-matches/${encodeURIComponent(id)}/${action}`
export async function proposeRankingMatch(id, slots) { return http(_rkm(id, 'propose'), _json({ slots })) }
export async function acceptRankingMatch(id, slot) { return http(_rkm(id, 'accept'), _json({ slot })) }
export async function declineRankingMatch(id) { return http(_rkm(id, 'decline'), _json({})) }
export async function setRankingCourt(id, court) { return http(_rkm(id, 'court'), _json({ court })) }
export async function rankingNoAgreement(id, note = '') { return http(_rkm(id, 'no-agreement'), _json({ note })) }
export async function giveUpRankingMatch(id) { return http(_rkm(id, 'give-up'), _json({})) }
export async function confirmRankingResult(id, ok = true) { return http(_rkm(id, 'confirm'), _json({ ok })) }
export async function rankingMatchSeen(id) { return http(_rkm(id, 'seen'), _json({})) }
export async function rankingMatchWhatsapp(id) { return http(_rkm(id, 'whatsapp'), _json({})) }
export async function getRankingMatchHistory(id) { return http(_rkm(id, 'history')) }
// Panel de la Comisión (engine/ranking_committee.py)
export async function getRankingIncidents(rid, status = 'open') { return http(`/api/club/rankings/${encodeURIComponent(rid)}/incidents?status=${encodeURIComponent(status)}`) }
export async function dismissAllRankingIncidents(rid, kind, note = '') { return http(`/api/club/rankings/${encodeURIComponent(rid)}/incidents/dismiss-all`, _json({ kind, note })) }
export async function resolveRankingIncident(id, body) { return http(`/api/club/ranking-incidents/${encodeURIComponent(id)}/resolve`, _json(body)) }
export async function addRankingPenalty(phaseId, body) { return http(`/api/club/ranking-phases/${encodeURIComponent(phaseId)}/penalty`, _json(body)) }
export async function substituteRankingPlayer(pairId, body) { return http(`/api/club/ranking-pairs/${encodeURIComponent(pairId)}/substitute`, _json(body)) }
export async function withdrawRankingPair(pairId) { return http(`/api/club/ranking-pairs/${encodeURIComponent(pairId)}/withdraw`, _json({})) }
export async function createClubTournament(body) { return http('/api/club/tournaments', _json(body)) }
// draw: torneo por grupos, cuadro "M" (masculino) o "F" (femenino)
export async function joinTournament(id, { partnerId = null, partnerGuest = '', draw = null, code = null } = {}) {
  return http(`/api/club/tournaments/${encodeURIComponent(id)}/join`, _json({ partner_id: partnerId, partner_guest: partnerGuest, draw, code }))
}
export async function leaveTournament(id) { return http(`/api/club/tournaments/${encodeURIComponent(id)}/leave`, _json({})) }
export async function addToTournament(id, { playerId = null, guestName = '', partnerId = null, partnerGuest = '', group = null, draw = null, level = null, partnerLevel = null } = {}) {
  return http(`/api/club/tournaments/${encodeURIComponent(id)}/players`, _json({ player_id: playerId, guest_name: guestName, partner_id: partnerId, partner_guest: partnerGuest, group, draw, level, partner_level: partnerLevel }))
}
// Nivel aproximado (0-100) de un invitado sin cuenta, mientras la inscripción está abierta
export async function setTournamentGuestLevel(id, entryId, level) {
  return http(`/api/club/tournaments/${encodeURIComponent(id)}/guest-level`, _json({ entry_id: entryId, level }))
}
// Round-Robin: el organizador pone una pareja en el grupo 1 o 2 (o null: sin grupo)
export async function setTournamentGroup(id, entryId, group) { return http(`/api/club/tournaments/${encodeURIComponent(id)}/group`, _json({ entry_id: entryId, group })) }
export async function removeFromTournament(id, entryId) {
  return http(`/api/club/tournaments/${encodeURIComponent(id)}/players/${encodeURIComponent(entryId)}`, { method: 'DELETE' })
}
export async function closeTournament(id) { return http(`/api/club/tournaments/${encodeURIComponent(id)}/close`, _json({})) }
export async function reportTournamentResult(id, matchId, gamesA, gamesB) {
  return http(`/api/club/tournaments/${encodeURIComponent(id)}/matches/${encodeURIComponent(matchId)}/result`, _json({ games_a: gamesA, games_b: gamesB }))
}
export async function substituteTournament(id, body) { return http(`/api/club/tournaments/${encodeURIComponent(id)}/substitute`, _json(body)) }
export async function setTournamentReferee(id, playerId, on) { return http(`/api/club/tournaments/${encodeURIComponent(id)}/referees`, _json({ player_id: playerId, on })) }
export async function withdrawTournamentPair(id, entryId) { return http(`/api/club/tournaments/${encodeURIComponent(id)}/withdraw-pair`, _json({ entry_id: entryId })) }
export async function noShowTournament(id, entryId, voidPlayed = false) { return http(`/api/club/tournaments/${encodeURIComponent(id)}/no-show`, _json({ entry_id: entryId, void_played: voidPlayed })) }
// Round-Robin por parejas: el organizador cambia de grupo dos parejas (antes de los resultados)
export async function swapTournamentGroups(id, entryA, entryB) { return http(`/api/club/tournaments/${encodeURIComponent(id)}/swap`, _json({ entry_a: entryA, entry_b: entryB })) }
export async function cancelTournament(id) { return http(`/api/club/tournaments/${encodeURIComponent(id)}`, { method: 'DELETE' }) }

export async function getClubPlayers() {
  return http('/api/club/matches/players')
}
export async function getMyClubMatches() {
  return http('/api/club/matches/mine')
}
export async function getClubMatch(id) {
  return http(`/api/club/matches/${encodeURIComponent(id)}`)
}
const _json = (body) => ({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
export async function createClubMatch(body) {
  return http('/api/club/matches', _json(body))
}
export async function joinClubMatch(id, slot = null) {
  return http(`/api/club/matches/${encodeURIComponent(id)}/join`, _json({ slot }))
}
export async function leaveClubMatch(id) {
  return http(`/api/club/matches/${encodeURIComponent(id)}/leave`, _json({}))
}
export async function addToClubMatch(id, { slot = null, playerId = null, guestName = '' }) {
  return http(`/api/club/matches/${encodeURIComponent(id)}/players`, _json({ slot, player_id: playerId, guest_name: guestName }))
}
export async function removeFromClubMatch(id, entryId) {
  return http(`/api/club/matches/${encodeURIComponent(id)}/players/${encodeURIComponent(entryId)}`, { method: 'DELETE' })
}
export async function cancelClubMatch(id) {
  return http(`/api/club/matches/${encodeURIComponent(id)}`, { method: 'DELETE' })
}
export async function setNotifyMatches(enabled) {
  return http('/api/player/notify-matches', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled }) })
}
